import React, { useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { IBackendCartEntries, IServerOrder } from '@rbi-ctg/menu';
import { ModalRating } from 'components/modal-rating';
import { OrderCancelledModal } from 'components/order-cancelled-modal';
import { useDialogModal } from 'hooks/use-dialog-modal';
import { useEffectOnUnmount } from 'hooks/use-effect-on-unmount';
import { useErrorModal } from 'hooks/use-error-modal';
import { useEtaCounter } from 'hooks/use-eta-counter/';
import { useOrderNumber } from 'hooks/use-order-number';
import { usePolling } from 'hooks/use-polling';
import { CartEntry } from 'pages/account/account-orders/details-modal/cart-entry';
import { StyledCartEntries } from 'pages/account/account-orders/details-modal/details-modal.styled';
import { getRewardsDiscount } from 'pages/account/account-orders/details-modal/receipt-details/get-rewards-discount';
import { FEE_TYPE } from 'pages/cart/your-cart/totals/constants';
import { useTotalTipCalculations } from 'pages/cart/your-cart/totals/use-total-tip-calculations';
import { filterFees } from 'pages/cart/your-cart/totals/utils';
import { deliveryStatusQueryOptions } from 'remote/api/delivery-status';
import { useDeliveryConfirmationContext } from 'state/delivery-confirmation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { DeliveryStatus, OrderStatus } from 'state/order/types';
import { CASH_ACCOUNT_IDENTIFIER } from 'state/payment/constants';
import { PaymentMethodBrand } from 'state/payment/types';
import { useServiceModeContext } from 'state/service-mode';
import { ServiceMode } from 'state/service-mode/types';
import { useUIContext } from 'state/ui';
import { calculateFeeValue, computeDeliveryFee } from 'utils/cart/helper';
import { readableHour } from 'utils/restaurant';
import { routes } from 'utils/routing';

import { CashPaymentReminder } from '../cash-payment-reminder';

import { CourierDetail } from './courier-detail';
import { DeliveryProgress } from './delivery-progress';
import {
  Card,
  CardCentralized,
  CardHeading,
  CardText,
  DeliveryCardContainer,
  ETA,
  FeeDeliveryItem,
  Pincode,
  PincodeMesssage,
  TextGroup,
  TotalRow,
} from './styled';

interface IDeliveryDetailsProps {
  serverOrder: IServerOrder;
}

// this list is not exhaustive, more statuses can be added
const failedDeliveryStatuses = [
  DeliveryStatus.ORDER_TIMEOUT,
  DeliveryStatus.ORDER_ERROR,
  DeliveryStatus.ORDER_CANCELLED,
];

const NETWORK_ERROR_NAME = 'NetworkError';
const NETWORK_ERROR_MESSAGE = 'network connection unavailable';

export const DeliveryDetails: React.FC<IDeliveryDetailsProps> = ({ serverOrder }) => {
  const { delivery, rbiOrderId, status } = serverOrder;
  const [deliveryStatus, setDeliveryStatus] = useState(delivery!.status);
  const [rbiOrderStatus, setRbiOrderStatus] = useState(status);
  const [dropoff, setDropoff] = useState(delivery?.dropoff);
  const { formatMessage } = useIntl();
  const { formatCurrencyForLocale } = useUIContext();
  const {
    setBringgUuids,
    driver,
    setDriver,
    deliveryVendor,
    setDeliveryVendor,
    pincode,
    setPincode,
  } = useDeliveryConfirmationContext();
  const eta = useEtaCounter(deliveryStatus, dropoff, deliveryVendor);
  const navigate = useNavigate();
  const isCashPayment = serverOrder.cart.payment.cardType === CASH_ACCOUNT_IDENTIFIER;
  const orderNumber = useOrderNumber(serverOrder);
  const { serviceMode } = useServiceModeContext();
  const { additionalDiscountAmount } = useTotalTipCalculations({ serverOrder, serviceMode });

  const enableHideTaxLine = useFlag(LaunchDarklyFlag.ENABLE_HIDE_TAX_LINE);
  const hideTipAmount = useFlag(LaunchDarklyFlag.HIDE_TIP_AMOUNT_CHECKOUT);
  const enableDiscountsOnOrderReceipt = useFlag(LaunchDarklyFlag.ENABLE_DISCOUNTS_ON_ORDER_RECEIPT);
  const enableFeesOnOrderReceipt = useFlag(LaunchDarklyFlag.ENABLE_FEES_ON_ORDER_RECEIPT);
  const enableOverrideDeliveryTime = useFlag(LaunchDarklyFlag.ENABLE_OVERRIDE_DELIVERY_TIME);
  const hideCourierDetails = useFlag(LaunchDarklyFlag.HIDE_COURIER_DETAILS);
  const enableFeesUi = useFlag(LaunchDarklyFlag.ENABLE_FEES_UI);
  const enableHideServiceFees = useFlag(LaunchDarklyFlag.ENABLE_HIDE_SERVICE_FEES_BY_RESTAURANT);

  const shouldShowAppRating = serverOrder.status === OrderStatus.INSERT_SUCCESSFUL;
  const isGuestOrder = serverOrder.cart.guestId;

  const rewardsDiscountCents = useMemo(
    () =>
      getRewardsDiscount({
        discounts: serverOrder.cart.discounts || [],
        enableDiscountsOnOrderReceipt,
      }),
    [serverOrder, enableDiscountsOnOrderReceipt]
  );

  const feesList = filterFees(serverOrder.cart.fees);

  const { stopPolling } = usePolling({
    queryOptions: deliveryStatusQueryOptions(rbiOrderId),
    interval: 5000,
    retry: 3,
    onResponse: data => {
      setDropoff(data?.delivery?.dropoff);
      setDeliveryStatus(data?.delivery?.status);
      setRbiOrderStatus(data?.status);
      setDriver(data?.delivery?.driver);
      setDeliveryVendor(data?.delivery?.vendorProperties?.vendor);
      setPincode(data?.delivery?.pincode);
      // Needed to track bringg delivery driver coords
      const share_uuid = data?.delivery?.shareUuid;
      const order_uuid = data?.delivery?.task?.uuid;
      setBringgUuids({ share_uuid, order_uuid });
    },
    onError: error => {
      if (
        error.name !== NETWORK_ERROR_NAME &&
        error.message.toLowerCase() !== NETWORK_ERROR_MESSAGE
      ) {
        openErrDialog({
          message: 'There was an error fetching your delivery status. Please try again later.',
        });
      }
    },
  });

  const [ErrorDialog, openErrDialog] = useErrorModal({
    onConfirm: () => navigate(routes.orders),
    modalAppearanceEventMessage: 'Error: Fetching Delivery Details Failure',
  });

  const [CancellationModal, openCancellationModal] = useDialogModal({
    Component: OrderCancelledModal,
    onOpen: stopPolling,
    onDismiss: () => navigate(routes.base),
  });

  useEffect(() => {
    if (
      deliveryStatus === DeliveryStatus.ORDER_CANCELLED ||
      deliveryStatus === DeliveryStatus.ORDER_ERROR
    ) {
      openCancellationModal();
    }
  }, [deliveryStatus, openCancellationModal]);

  useEffectOnUnmount(() => {
    stopPolling();
  });

  if (!delivery) {
    return null;
  }

  const cardType = serverOrder?.cart?.payment?.cardType || null;
  const ccLast4 = serverOrder?.cart?.payment?.ccLast4 || null;
  const paymentMethodBrand = serverOrder?.cart?.payment?.paymentMethodBrand || null;

  const feeCents = delivery?.feeCents || 0;
  const feeDiscountCents = delivery?.feeDiscountCents || 0;
  const geographicalFeeCents = delivery?.geographicalFeeCents || 0;
  const serviceFeeCents = delivery.serviceFeeCents || 0;
  const serviceFeeDiscountCents = delivery.serviceFeeDiscountCents || 0;
  const smallCartFeeCents = delivery?.smallCartFeeCents || 0;

  const deliveryFeeCents = enableFeesUi
    ? calculateFeeValue(feeCents, feeDiscountCents)
    : computeDeliveryFee({
        feeCents,
        feeDiscountCents,
        geographicalFeeCents,
        serviceFeeCents,
        smallCartFeeCents,
      });
  const isFreeDelivery = deliveryFeeCents === 0;
  const DeliveryFee = () =>
    isFreeDelivery && !enableFeesUi ? (
      <FeeDeliveryItem tabIndex={0}>
        {formatMessage({ id: 'free' })}
        &nbsp;<s>{formatCurrencyForLocale(feeDiscountCents)}</s>
      </FeeDeliveryItem>
    ) : (
      <CardText tabIndex={0}>{formatCurrencyForLocale(deliveryFeeCents)}</CardText>
    );

  const getDeliveryEtaText = (status: DeliveryStatus): string => {
    switch (status) {
      case DeliveryStatus.DRIVER_AT_CUSTOMER:
        return formatMessage({ id: 'deliveryDriverArrived' });
      case DeliveryStatus.ORDER_DROPPED_OFF:
        return formatMessage({ id: 'deliveryDelivered' });
      default:
        return enableOverrideDeliveryTime ? '' : eta || '...';
    }
  };

  const showCashPaymentMessage =
    isCashPayment &&
    serverOrder.cart.totalCents > 0 &&
    !failedDeliveryStatuses.includes(deliveryStatus) &&
    paymentMethodBrand !== PaymentMethodBrand.PAYMENT_ON_DELIVERY_CARD;

  // TODO: check for cancelled orders and orders that have already been delivered
  return (
    <>
      {showCashPaymentMessage && <CashPaymentReminder serviceMode={ServiceMode.DELIVERY} />}
      <DeliveryCardContainer>
        {pincode && (
          <CardCentralized>
            <CardHeading>{formatMessage({ id: 'pincodeTitle' })}</CardHeading>
            <PincodeMesssage>{formatMessage({ id: 'pincodeDescription' })}</PincodeMesssage>
            <Pincode data-testid="order-confirmation-pincode">{pincode}</Pincode>
          </CardCentralized>
        )}
        <Card>
          <ETA tabIndex={0}>
            {enableOverrideDeliveryTime
              ? formatMessage({ id: 'etaFixed' })
              : formatMessage({ id: 'eta' }) + ': '}
            <strong data-testid="order-preparation-confirmation-eta">
              {getDeliveryEtaText(deliveryStatus)}
            </strong>
          </ETA>
          <CardHeading
            tabIndex={0}
            data-testid="order-preparation-confirmation"
            data-private
            data-dd-privacy="mask"
          >
            {formatMessage({ id: 'deliveringToYourDoorAt' })}
            <br />
            {delivery.dropoff.addressLine1}
          </CardHeading>
          <DeliveryProgress
            deliveryStatus={deliveryStatus}
            rbiOrderStatus={rbiOrderStatus}
            rbiOrderId={serverOrder.rbiOrderId}
          />
          {!hideCourierDetails && driver && <CourierDetail driver={driver} />}
        </Card>

        <Card>
          <CardHeading tabIndex={0}>{formatMessage({ id: 'orderDetailsHeading' })}</CardHeading>
          <TextGroup>
            {orderNumber ? (
              <CardText tabIndex={0}>
                {formatMessage({ id: 'orderNumber' })}: {orderNumber}
              </CardText>
            ) : null}
            {cardType && ccLast4 && (
              <CardText tabIndex={0}>
                {formatMessage(
                  { id: 'paymentXEndingInY' },
                  {
                    cardType,
                    ccLast4,
                  }
                )}
              </CardText>
            )}
            <CardText tabIndex={0}>
              {formatMessage({ id: 'orderTime' })}: {readableHour(serverOrder.createdAt, true)}
            </CardText>
          </TextGroup>

          <CardHeading tabIndex={0}>{formatMessage({ id: 'deliveryInfo' })}</CardHeading>
          <TextGroup>
            {!isGuestOrder && (
              <CardText data-private data-dd-privacy="mask" tabIndex={0}>
                <strong>{serverOrder.cart.customerName}</strong>
              </CardText>
            )}
            <CardText data-private data-dd-privacy="mask" tabIndex={0}>
              {delivery.dropoff.addressLine1}
              <br />
              {delivery.dropoff.addressLine2 && (
                <>
                  {formatMessage(
                    { id: 'unitNumber' },
                    { unitNumber: delivery.dropoff.addressLine2 }
                  )}
                  <br />
                </>
              )}
              {delivery.dropoff.city}, {delivery.dropoff.state} {delivery.dropoff.zip}
            </CardText>
            {delivery.dropoff.phoneNumber && (
              <CardText tabIndex={0} data-private data-dd-privacy="mask">
                {delivery.dropoff.phoneNumber}
              </CardText>
            )}
            {delivery.instructions && (
              <>
                <br />
                <CardText data-private data-dd-privacy="mask" tabIndex={0}>
                  {formatMessage({ id: 'instructions' })}: {delivery.instructions}
                </CardText>
              </>
            )}
          </TextGroup>

          <CardHeading tabIndex={0}>{formatMessage({ id: 'yourOrder' })}</CardHeading>
          <TextGroup>
            <StyledCartEntries>
              {serverOrder.cart.cartEntries.map((cartEntry: IBackendCartEntries, idx: number) => (
                <CartEntry entry={cartEntry} key={cartEntry.name! + idx} index={idx} />
              ))}
            </StyledCartEntries>
          </TextGroup>

          {!!rewardsDiscountCents && (
            <TotalRow>
              <CardText>
                {formatMessage({
                  id: 'rewardSavings',
                })}
              </CardText>
              <CardText>{formatCurrencyForLocale(-rewardsDiscountCents)}</CardText>
            </TotalRow>
          )}
          {!!additionalDiscountAmount && (
            <TotalRow>
              <CardText>
                {formatMessage({
                  id: 'comboOfferSavings',
                })}
              </CardText>
              <CardText>{formatCurrencyForLocale(-additionalDiscountAmount)}</CardText>
            </TotalRow>
          )}

          <TotalRow>
            <CardText tabIndex={0}>{formatMessage({ id: 'subtotal' })}</CardText>
            <CardText tabIndex={0} data-testid="delivery-subtotal">
              {formatCurrencyForLocale(serverOrder.cart.subTotalCents)}
            </CardText>
          </TotalRow>
          {!enableHideTaxLine && (
            <TotalRow>
              <CardText tabIndex={0}>{formatMessage({ id: 'tax' })}</CardText>
              <CardText tabIndex={0}>{formatCurrencyForLocale(serverOrder.cart.taxCents)}</CardText>
            </TotalRow>
          )}
          {(serviceFeeCents > 0 || enableFeesUi) && !enableHideServiceFees && (
            <TotalRow>
              <CardText tabIndex={0}>{formatMessage({ id: 'serviceFee' })}</CardText>
              <CardText tabIndex={0}>
                {formatCurrencyForLocale(
                  calculateFeeValue(serviceFeeCents, serviceFeeDiscountCents)
                )}
              </CardText>
            </TotalRow>
          )}
          <TotalRow>
            <CardText tabIndex={0}>{formatMessage({ id: 'deliveryFee' })}</CardText>
            <DeliveryFee />
          </TotalRow>
          {geographicalFeeCents > 0 && (
            <TotalRow>
              <CardText tabIndex={0}>{formatMessage({ id: 'geographicalFee' })}</CardText>
              <CardText tabIndex={0}>{formatCurrencyForLocale(geographicalFeeCents)}</CardText>
            </TotalRow>
          )}
          {smallCartFeeCents > 0 && (
            <TotalRow>
              <CardText tabIndex={0}>{formatMessage({ id: 'smallCartFee' })}</CardText>
              <CardText tabIndex={0}>{formatCurrencyForLocale(smallCartFeeCents)}</CardText>
            </TotalRow>
          )}
          {!hideTipAmount && (
            <TotalRow data-testid="tipCents">
              <CardText tabIndex={0}>{formatMessage({ id: 'tip' })}</CardText>
              <CardText tabIndex={0}>{formatCurrencyForLocale(delivery.tipCents)}</CardText>
            </TotalRow>
          )}
          {enableFeesOnOrderReceipt &&
            feesList.map(feeItem => (
              <TotalRow key={feeItem.name}>
                <CardText>{formatMessage({ id: FEE_TYPE[feeItem.name] })}</CardText>

                <CardText>{formatCurrencyForLocale(feeItem.totalCents)}</CardText>
              </TotalRow>
            ))}
          <br />
          <TotalRow>
            <CardText tabIndex={0}>
              <strong>{formatMessage({ id: 'total' })}</strong>
            </CardText>
            <CardText tabIndex={0}>
              <strong>{formatCurrencyForLocale(serverOrder.cart.totalCents)}</strong>
            </CardText>
          </TotalRow>
        </Card>
        {shouldShowAppRating && <ModalRating />}
      </DeliveryCardContainer>
      <ErrorDialog />
      <CancellationModal />
    </>
  );
};
